import React, { useEffect, useRef, useState } from "react";
import './shift.scss';
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import DataTable from "react-data-table-component";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "../../api/axios";
import { destroySession } from "../../helpers/Session";
import ShiftLoader from "../../components/loader/ShiftLoader";
import toast from "../../components/sweetalert/Sweetalert";
import Swal from "sweetalert2";

const Shifts = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const timeoutRef = useRef(null);

    const accesstoken = sessionStorage.getItem('accesstoken');
    const uname = sessionStorage.getItem('uname');

    const [currentPage, setCurrentPage] = useState(1);
    const [rowPerPage, setRowPerPage] = useState(10);

    const [formSubmit, setFormSubmit] = useState(false);
    const [formData, setFormData] = useState({
        id: 0,
        shift: '',
        status: ''
    });

    const shiftDataKey = ['shifts', currentPage, rowPerPage];

    const tableColumns = [
        { name: 'Shift', selector: row => row.shift, sortable: true },
        { name: 'Status', selector: row => row.status },
        { name: 'Action', selector: row => row.action }
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    }

    const resetForm = () => {
        setFormData({
            id: 0,
            shift: '',
            status: ''
        });
    }

    const deleteShiftMutation = useMutation({
        mutationFn: async (id) => {
            const res = await axios.delete(`shifts?id=${id}&uname=${uname}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        onSuccess: (data) => {
            toast.fire({ icon: 'success', title: data });
            queryClient.invalidateQueries(shiftDataKey);
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.response || 'Something went wrong while deleting shift!';
            toast.fire({ icon: 'error', title: errorMessage });

            if (error.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const deleteShift = (id, shift) => {
        Swal.fire({
			text: `Are you sure you want to delete shift details of ${shift}?`,
			icon: 'warning',
            allowOutsideClick: false,
			showCancelButton: true,
			confirmButtonColor: '#3085d6',
			cancelButtonColor: '#d33',
			confirmButtonText: 'Yes',
			cancelButtonText: 'No',
			showLoaderOnConfirm: true,
			preConfirm: function(result) {
				return new Promise(function(resolve) {
			        if (result) {
                        deleteShiftMutation.mutate(id);
                        resolve();
					}  
		      	});
			}
		});
    }

    const { data: shifts, isLoading: isLoadingShifts, error: shiftsError } = useQuery({
        queryKey: shiftDataKey,
        queryFn: async () => {
            const res = await axios.get(`shifts?page=${currentPage}&rows_per_page=${rowPerPage}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            const data = res.data.response?.shifts?.map(item => ({
                shift: item.shift,
                status: (item.status === 1) ? <span className="label label-success">Active</span> : <span className="label label-danger">Inactive</span>,
                action: (
                    <div>
                        <button type="button" className="btn-edit" title="Edit" onClick={() => { setFormData({ id: item.id, shift: item.shift, status: item.status }) }}><i className="fa fa-edit"></i></button>
                        <button type="button" className="btn-delete" title="Delete" onClick={() => { deleteShift(item.id, item.shift) }}><i className="fa fa-trash"></i></button>
                    </div>
                )
            }));

            return { data, totalRows: res.data.response?.totalRows };
        },
        enabled: !!accesstoken,
        retry: 1
    });

    useEffect(() => {
        if (shiftsError?.response?.data.status === 'inactive') {
            destroySession();
            clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
        }
    });

    const handlePageChange = (page) => {
        setCurrentPage(page);
    }

    const handleRowsPerPageChange = (newPerPage) => {
        setRowPerPage(newPerPage);
        setCurrentPage(1);
    };

    const submitShiftMutation = useMutation({
        mutationFn: async (shiftData) => {
            const res = await axios.post('shifts', shiftData, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        onSuccess: (data) => {
            resetForm();
            setFormSubmit(false);
            toast.fire({ icon: 'success', title: data });
            queryClient.invalidateQueries(shiftDataKey);
        },
        onError: (error) => {
            setFormSubmit(false);
            const errorMessage = error.response?.data?.response || 'Something went wrong while saving shift details!';
            toast.fire({ icon: 'error', title: errorMessage });
            if (error.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const handleSubmitShift = (e) => {
        const { shift, status } = formData;

        if (!shift || !status) {
            toast.fire({ icon: 'error', title: "All fields are required." });
            return;
        }

        setFormSubmit(true);
        submitShiftMutation.mutate({ ...formData, uname });
    }


    return (
        <div className="shifts">
            <div className="page-header">
                <div className='title'>
                    <h1>Shifts</h1>
                    <ul className='breadcrumbs'>
                        <li>
                            <NavLink to='/dtr/my-dtr'>DTR</NavLink>
                        </li>
                        <li>
                            <span>Shifts</span>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="panels">
                <div className="left-panel">
                    <DataTable 
                        columns={tableColumns} 
                        data={shifts?.data || []} 
                        progressPending={isLoadingShifts} 
                        progressComponent={<ShiftLoader />} 
                        pagination 
                        paginationTotalRows={shifts?.totalRows || 0} 
                        onChangeRowsPerPage={handleRowsPerPageChange}
                        onChangePage={handlePageChange} 
                        dense 
                        highlightOnHover 
                        responsive />
                </div>
                <div className="right-panel">
                    <form>
                        <div className="form-group">
                            <label>Shift Schedule</label>
                            <input type="text" name="shift" autoComplete="off" value={formData.shift} onChange={handleInputChange} disabled={formSubmit} />
                        </div>
                        <div className='form-group'>
                            <label>Status</label>
                            <select name='status' value={formData.status} onChange={handleInputChange} disabled={formSubmit}>
                                <option value=''></option>
                                <option value='1'>Active</option>
                                <option value='2'>Inactive</option>
                            </select>
                        </div>
                        <div className='button-group'>
                            <button type='button' onClick={handleSubmitShift} disabled={formSubmit}>
                                {formSubmit ? <span>SAVING <i className="fa fa-spinner fa-spin"></i></span> : 'SAVE'}
                            </button>
                            <button type='button' onClick={resetForm} disabled={formSubmit}>RESET</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

export default Shifts;